import { useState } from "react";
import dayjs from "dayjs";
import { db } from "../../lib/api";
import { useQuery } from "../../lib/hooks";
import { AREA_LABEL, type Area } from "../../lib/refdata";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, Seg, money } from "../../components/ui";
import { HBars } from "../../components/charts";

const STEPS = [["added", "Added"], ["reached", "Reached"], ["conversation", "Conversation"], ["appointment", "Appointment"], ["deal", "Deal opened"], ["won", "Won"]] as const;
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : "—");

/** Where people come from, and where they drop off: added → reached → talked → met → deal → won. */
export default function Funnel() {
  const [range, setRange] = useState<"30" | "90" | "year">("90");
  const from = range === "year" ? dayjs().startOf("year") : dayjs().subtract(Number(range), "day");
  const q = useQuery(`dash:funnel:${range}`, () => db.rpc<any>("funnel", { p_from: from.format("YYYY-MM-DD"), p_to: dayjs().format("YYYY-MM-DD") }));
  const d = q.data;
  const rows: any[] = d?.by_source ?? [];
  const total = Object.fromEntries(STEPS.map(([k]) => [k, rows.reduce((s, r) => s + Number(r[k] ?? 0), 0)]));
  const leak = STEPS.slice(1).map(([k, l], i) => ({ k, l, rate: total[STEPS[i][0]] ? total[k] / total[STEPS[i][0]] : null }))
    .filter((x) => x.rate != null).sort((a, b) => (a.rate ?? 1) - (b.rate ?? 1))[0];
  return (
    <>
      <PageHead title="Funnel" sub="People added to each list, and how far they got: reached, a real conversation, an appointment, a deal, won. Find where people leak, and set targets from your real conversion rates." />
      <div className="filters"><Seg value={range} onChange={setRange} options={[{ value: "30", label: "Last 30 days" }, { value: "90", label: "Last 90 days" }, { value: "year", label: "This year" }]} /></div>
      <ErrorBox error={q.error} />
      {!d ? <Loading rows={6} /> : rows.length === 0 ? <Panel><Empty title="No one added in this period" /></Panel> : (
        <div style={{ display: "grid", gap: 14 }}>
          <Panel title="All sources" hint={leak ? `Biggest leak: ${STEPS.find(([k]) => k === leak.k)?.[1] ?? leak.l} (${Math.round((leak.rate ?? 0) * 100)}% get through)` : undefined}>
            <div className="panel-body funnel">
              {STEPS.map(([k, l], i) => (
                <div key={k} className="fstep">
                  <div className="fbar" style={{ width: `${total.added ? Math.max(4, (total[k] / total.added) * 100) : 0}%` }}><b>{total[k]}</b></div>
                  <span>{l}{i > 0 && <span className="muted small"> · {pct(total[k], total[STEPS[i - 1][0]])} of {STEPS[i - 1][1].toLowerCase()}</span>}</span>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="By source">
            <div className="table-wrap"><table className="data">
              <thead><tr><th>Source</th>{STEPS.map(([k, l]) => <th key={k} className="num">{l}</th>)}<th className="num">Added → won</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.area}><td><strong>{AREA_LABEL[r.area as Area] ?? r.area}</strong></td>
                  {STEPS.map(([k], i) => <td key={k} className="num">{r[k]}{i > 0 && r[STEPS[i - 1][0]] ? <div className="small muted">{pct(r[k], r[STEPS[i - 1][0]])}</div> : null}</td>)}
                  <td className="num"><b>{pct(r.won, r.added)}</b></td></tr>))}</tbody>
            </table></div>
          </Panel>
          <div className="grid-2">
            <Panel title="Deals opened in the period" hint={`${d.deals.won} won · ${d.deals.lost} lost or not now · ${d.deals.open} still open`}>
              <div className="panel-body">
                <HBars rows={(d.by_deal_source ?? []).map((x: any) => ({ label: `${AREA_LABEL[x.source as Area] ?? x.source} (${x.won} won${x.premium ? `, ${money(x.premium)}` : ""})`, value: x.opened }))} color="var(--royal)" />
              </div>
            </Panel>
            <Panel title="Why deals were lost" hint="From the lost reason on each card">
              <div className="panel-body">
                {(d.lost_reasons ?? []).length === 0 ? <div className="small muted">No lost deals in this period.</div>
                  : <HBars rows={d.lost_reasons.map((x: any) => ({ label: x.reason, value: x.n }))} color="var(--alert)" />}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </>
  );
}
